import { AxiosError } from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';
import { getContractMergeProposalsRequest, mergeContractsRequest } from 'api/requests/contract-merge-requests';
import { createMergeTracker, MergeTrackedOutcome } from 'services/analytics/mergeTracking';
import {
  CONTRACT_MERGE__FAILED,
  CONTRACT_MERGE__NOTHING_TO_MERGE,
  CONTRACT_MERGE__TIMED_OUT,
} from 'translations/constants';
import { ContractMergeOutcome, IContractMergeProposal, IContractMergeResult } from 'types/contract/IContractMerge';

/**
 * - `closed`: nothing is going on.
 * - `loading`: the API is grouping the selection into proposed merges.
 * - `review`: the proposed merges are shown, the SuperAdmin can deselect some and confirm.
 * - `notice`: nothing can be merged (or the grouping failed); a message and no confirm action.
 * - `merging`: confirmed, waiting until every proposed merge has an outcome.
 * - `results`: every proposed merge has an outcome and at least one needs the SuperAdmin's attention.
 */
export type ContractMergePhase = 'closed' | 'loading' | 'review' | 'notice' | 'merging' | 'results';

export interface IContractMergeRow {
  proposal: IContractMergeProposal;
  result: IContractMergeResult;
}

interface UseContractMergeParams {
  companyId: number;
  /** Called once every proposed merge has an outcome, so the planning can be read again. */
  onFinished: () => void;
}

const trackedOutcomes: Record<ContractMergeOutcome, MergeTrackedOutcome> = {
  Merged: 'merged',
  MergedInUPayOnly: 'merged_in_upay_only',
  TimedOut: 'timed_out',
  Failed: 'failed',
  Locked: 'failed',
  SelectionChanged: 'failed',
  UPayUnavailable: 'failed',
  NotAvailable: 'failed',
};

/** A merge is only "done" for the planning when UPay and UPortal agree; anything else needs a look. */
export const isMergedRow = (row: IContractMergeRow): boolean => row.result.outcome === 'Merged';

const syntheticResult = (key: string, outcome: ContractMergeOutcome, translationKey: string): IContractMergeResult => ({
  key,
  outcome,
  durationMs: 0,
  dimonaWarning: false,
  messages: [{ translationKey }],
});

/**
 * The state of merging sequential daily contracts from the planning view: ask the API which of the selected days can
 * merge, let the SuperAdmin pick, hand the picked merges to the API (which hands them to UPay) and report the outcomes.
 */
const useContractMerge = ({ companyId, onFinished }: UseContractMergeParams) => {
  const [phase, setPhase] = useState<ContractMergePhase>('closed');
  const [proposals, setProposals] = useState<IContractMergeProposal[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [rows, setRows] = useState<IContractMergeRow[]>([]);
  const [noticeKey, setNoticeKey] = useState<string>(CONTRACT_MERGE__NOTHING_TO_MERGE);
  const [mergedAll, setMergedAll] = useState<boolean>(false);

  // a second click on Confirm must not start a second merge, even before the first render has disabled the button
  const confirmingRef = useRef<boolean>(false);
  const trackerRef = useRef(createMergeTracker());

  const start = useCallback(
    async (contractDateIds: number[], employeesCount: number) => {
      trackerRef.current = createMergeTracker();
      trackerRef.current.trackClicked(contractDateIds.length, employeesCount);

      setMergedAll(false);
      setPhase('loading');

      try {
        const response = await getContractMergeProposalsRequest({ companyId, contractDateIds });
        setProposals(response.proposals);
        setSelectedKeys(response.proposals.map(proposal => proposal.key));
        setNoticeKey(CONTRACT_MERGE__NOTHING_TO_MERGE);
        setPhase(response.proposals.length > 0 ? 'review' : 'notice');
      } catch {
        setProposals([]);
        setNoticeKey(CONTRACT_MERGE__FAILED);
        setPhase('notice');
      }
    },
    [companyId],
  );

  const toggle = useCallback((key: string) => {
    setSelectedKeys(current => (current.includes(key) ? current.filter(k => k !== key) : [...current, key]));
  }, []);

  const confirm = useCallback(async () => {
    const chosen = proposals.filter(proposal => selectedKeys.includes(proposal.key));

    if (confirmingRef.current || phase !== 'review' || chosen.length === 0) {
      return;
    }

    confirmingRef.current = true;
    trackerRef.current.trackConfirmed(proposals.length, proposals.length - chosen.length);
    setPhase('merging');

    let outcomes: IContractMergeRow[];

    try {
      const response = await mergeContractsRequest({
        companyId,
        merges: chosen.map(proposal => ({
          key: proposal.key,
          fingerprint: proposal.fingerprint,
          contractDateIds: proposal.days.map(day => day.contractDateId),
        })),
      });

      outcomes = chosen.map(proposal => ({
        proposal,
        result:
          response.results.find(result => result.key === proposal.key) ??
          syntheticResult(proposal.key, 'Failed', CONTRACT_MERGE__FAILED),
      }));
    } catch (error) {
      // Without an answer nobody knows how far UPay got, so the SuperAdmin is told to check there. An answer that is
      // an error (a refused request) means nothing was started.
      const answered = Boolean((error as AxiosError)?.response);

      outcomes = chosen.map(proposal => ({
        proposal,
        result: answered
          ? syntheticResult(proposal.key, 'Failed', CONTRACT_MERGE__FAILED)
          : syntheticResult(proposal.key, 'TimedOut', CONTRACT_MERGE__TIMED_OUT),
      }));
    }

    outcomes.forEach(({ proposal, result }) => {
      trackerRef.current.trackOutcome({
        outcome: trackedOutcomes[result.outcome] ?? 'failed',
        daysCount: proposal.days.length,
        errorKeys: result.messages.map(message => message.translationKey),
        durationMs: result.durationMs,
      });
    });

    const allMerged = outcomes.every(row => isMergedRow(row) && !row.result.dimonaWarning);

    setRows(outcomes);
    setMergedAll(allMerged);
    setPhase(allMerged ? 'closed' : 'results');
    confirmingRef.current = false;
    onFinished();
  }, [companyId, onFinished, phase, proposals, selectedKeys]);

  const close = useCallback(() => {
    if (confirmingRef.current) {
      return;
    }

    setPhase('closed');
    setProposals([]);
    setSelectedKeys([]);
    setRows([]);
  }, []);

  return useMemo(
    () => ({
      phase,
      proposals,
      selectedKeys,
      rows,
      noticeKey,
      mergedAll,
      start,
      toggle,
      confirm,
      close,
      dismissSuccess: () => setMergedAll(false),
    }),
    [phase, proposals, selectedKeys, rows, noticeKey, mergedAll, start, toggle, confirm, close],
  );
};

export default useContractMerge;
